import { inject } from '@angular/core';
import { RedirectFunction, Router, Routes } from '@angular/router';

import { DefaultComponent } from './core/components/default.component';
import { msalCanActivateChildGuard, msalCanActivateGuard } from './core/guards/msal.guard';

const redirectToSubroute: RedirectFunction = ({ queryParams }) => {
  // NOTE: subroute is defined in electron for tiling-canvas routing, since we can't access to a standard Angular route directly.
  const subroute = queryParams.subroute;

  if (subroute) {
    return `/${subroute}`;
  }

  // NOTE: when no file is loaded the primary outlet is empty,
  // so this redirect also matches ribbon-only navigations like '/(ribbon:ribbon/report)'.
  const ribbon = inject(Router).currentNavigation()?.extractedUrl.root.children.ribbon;

  return ribbon ? `/(ribbon:${ribbon})` : '/(ribbon:ribbon)';
};

export const appRoutes: Routes = [
  {
    path: '',
    component: DefaultComponent,
    canActivate: [msalCanActivateGuard],
    canActivateChild: [msalCanActivateChildGuard],
    children: [
      {
        path: '',
        redirectTo: redirectToSubroute,
        pathMatch: 'full',
      },
      {
        path: 'layout',
        loadChildren: () => import('./modules/layout/layout.module').then(x => x.LayoutModule),
      },
      {
        path: 'aside',
        outlet: 'aside',
        children: [
          {
            path: 'aside',
            loadChildren: () => import('./modules/aside-wrapper/aside-wrapper.module').then(x => x.AsideWrapperModule),
          },
        ],
      },
      {
        path: 'table',
        outlet: 'table',
        children: [
          {
            path: 'indications-table',
            loadChildren: () => import('./modules/indications-table/indications-table.module').then(x => x.WindviewIndicationsTableModule),
          },
        ],
      },
      {
        path: 'ribbon',
        outlet: 'ribbon',
        children: [
          {
            path: '',
            data: { disableQuickAction: true },
            loadChildren: () => import('./modules/ribbon/ribbon-home/ribbon-home.module').then(x => x.RibbonHomeModule),
          },
          {
            path: 'inspect',
            loadChildren: () => import('./modules/ribbon/ribbon-inspect/ribbon-inspect.module').then(x => x.RibbonInspectModule),
          },
          {
            path: 'ut-settings',
            loadChildren: () => import('./modules/ribbon/ribbon-ut-settings/ribbon-ut-settings.module').then(x => x.RibbonUTSettingsModule),
          },
          {
            path: 'gates',
            loadChildren: () => import('./modules/ribbon/ribbon-gates/ribbon-gates.module').then(x => x.RibbonGatesModule),
          },
          {
            path: 'tcg',
            loadChildren: () => import('./modules/ribbon/ribbon-tcg/ribbon-tcg.module').then(x => x.RibbonTcgModule),
          },
          {
            path: 'scanner',
            loadChildren: () => import('./modules/ribbon/ribbon-scanner/ribbon-scanner.module').then(x => x.RibbonScannerModule),
          },
          {
            path: 'report',
            loadChildren: () => import('./modules/ribbon/ribbon-report/ribbon-report.module').then(x => x.RibbonReportModule),
          },
          {
            path: 'analysis',
            loadComponent: () => import('./modules/ribbon/ribbon-analysis/ribbon-analysis.component').then(x => x.RibbonAnalysisComponent),
          },
          {
            path: 'display',
            loadChildren: () => import('./modules/ribbon/ribbon-display/ribbon-display.module').then(x => x.RibbonDisplayModule),
          },
        ],
      },
      {
        path: 'modal',
        outlet: 'modal',
        children: [
          {
            path: 'network-settings',
            loadChildren: () => import('@olympus/ngx-shared').then(x => x.NetworkSettingsModule),
            data: {
              showDeviceSettings: false,
            },
          },
        ],
      },
      {
        path: 'manager',
        outlet: 'manager',
        children: [
          {
            path: 'device-manager',
            loadChildren: () => import('@olympus/ngx-shared').then(x => x.DeviceManagerModule),
          },
          {
            path: 'layout-manager',
            loadChildren: () => import('@olympus/ngx-shared').then(x => x.LayoutManagerModule),
          },
          {
            path: 'palette-manager',
            loadChildren: () => import('@olympus/ngx-shared').then(x => x.PaletteManagerModule),
          },
          {
            path: 'calibration',
            loadChildren: () => import('./modules/calibration/app-calibration.module').then(x => x.AppCalibrationModule),
          },
          {
            path: 'firing-sequence-manager',
            loadChildren: () => import('@olympus/ngx-shared').then(x => x.FiringSequenceManagerModule),
          },
        ],
      },
    ],
  },
];
